import { useQuery, type QueryState } from "../analytics";
import { useIdentityClient } from "./client";
import type {
  ApiKey,
  Invite,
  Organization,
  OrgMember,
  Workspace,
} from "./types";

/* Reads share the analytics useQuery cache. Mutations don't go through here —
 * callers invoke the client (via useAction) then bump `version` to force a
 * refetch, same as the agents/configs modules. Identity data is org-scoped
 * (or carries its workspace id explicitly), so keys are NOT prefixed with the
 * active workspace. */

export function useWorkspaces(version = 0): QueryState<Workspace[]> {
  const client = useIdentityClient();
  return useQuery(
    () => client.listWorkspaces(),
    `identity:workspaces:v${version}`,
  );
}

export function useOrganization(): QueryState<Organization> {
  const client = useIdentityClient();
  return useQuery(() => client.getOrganization(), "identity:org");
}

/** Resolve a pending invitation by id (to show which org it grants). Keyed by
 *  the id so switching invites refetches; accepting is a mutation done via
 *  useAction, not here. */
export function useInvite(invitationId: string): QueryState<Invite> {
  const client = useIdentityClient();
  return useQuery(
    () => client.getInvite(invitationId),
    `identity:invite:${invitationId}`,
  );
}

export function useOrgMembers(version = 0): QueryState<OrgMember[]> {
  const client = useIdentityClient();
  return useQuery(
    () => client.listOrgMembers(),
    `identity:members:v${version}`,
    "loadMembers",
  );
}

/** One workspace's slice of the cross-workspace key list. Failures stay per
 *  workspace — one inaccessible workspace must not blank the whole page. */
export interface WorkspaceKeys {
  workspaceId: string;
  keys?: ApiKey[];
  error?: unknown;
}

/** The viewer's API keys across all their workspaces. The identity API only
 *  lists per workspace (GET /identity/api-keys?workspace_id=…), so this fans
 *  out one request per workspace and merges, preserving input order. */
export function useAllApiKeys(
  workspaceIds: string[],
  version = 0,
): QueryState<WorkspaceKeys[]> {
  const client = useIdentityClient();
  return useQuery(
    () =>
      Promise.all(
        workspaceIds.map((workspaceId) =>
          client
            .listApiKeys(workspaceId)
            .then((keys): WorkspaceKeys => ({ workspaceId, keys }))
            .catch((error): WorkspaceKeys => ({ workspaceId, error })),
        ),
      ),
    `identity:keys:${workspaceIds.join(",")}:v${version}`,
    "loadApiKeys",
  );
}
